import React, { useState } from "react";
import { Box, Button, Container, Tab, Tabs } from "@mui/material";
import { FileSpreadsheet } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import AdminSideNav from "../../../adminComponents/sidenav";
import AdminHeader from "../../../adminComponents/header";
import LiveDashboard from "./LiveDashboard";
import AgentRoster from "./AgentRoster";
import AttendanceRecords from "./AttendanceRecords";
import BreakTypes from "./BreakTypes";
import AttendanceReport from "./AttendanceReport";
import AttendanceCorrections from "./AttendanceCorrections";
import HrmsSync from "./HrmsSync";

// /admin/agents -- the live floor, the roster, and the attendance records.
// The tab lives in the query string so a supervisor can bookmark or share the
// live view directly.

const BASE_TABS = ["live", "roster", "records", "report", "break-types"];

const AdminAgents = () => {
  // Corrections change what payroll sees, so only superadmins get the tab.
  // The API enforces the same rule; hiding it here just avoids a dead screen.
  const isSuper = localStorage.getItem("role") === "superadmin";
  const TABS = isSuper ? [...BASE_TABS, "corrections", "hrms-sync"] : BASE_TABS;
  const [params, setParams] = useSearchParams();
  const initial = TABS.indexOf(params.get("tab"));
  const [tab, setTab] = useState(initial === -1 ? 0 : initial);

  const change = (_, value) => {
    setTab(value);
    setParams({ tab: TABS[value] }, { replace: true });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ ml: { xs: "0px", md: "200px" } }}>
        <AdminSideNav />
        <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <AdminHeader title="Agents & Attendance" />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            background: "linear-gradient(135deg, #f5f7fa 0%, #fdfdfdff 100%)",
            minHeight: "100vh",
            py: { xs: 2, md: 3 },
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                mb: 3,
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <Tabs
                value={tab}
                onChange={change}
                sx={{
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
                  "& .Mui-selected": { color: "#0C237B !important" },
                  "& .MuiTabs-indicator": { backgroundColor: "#0C237B" },
                }}
              >
                <Tab label="Live Dashboard" />
                <Tab label="Manage Agents" />
                <Tab label="Attendance Records" />
                <Tab label="Reports & Export" />
                <Tab label="Break Types" />
                {isSuper && <Tab label="Corrections" />}
                {isSuper && <Tab label="HRMS Sync" />}
              </Tabs>

              {/* A shortcut to the report tab from wherever you are. The
                  report itself is a table you can read before downloading. */}
              <Button
                variant="outlined"
                startIcon={<FileSpreadsheet size={17} />}
                onClick={() => change(null, TABS.indexOf("report"))}
                sx={{
                  mb: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  borderColor: "#0C237B",
                  color: "#0C237B",
                  "&:hover": { borderColor: "#1a3399", bgcolor: "#eef2ff" },
                }}
              >
                Export Report
              </Button>
            </Box>

            {tab === 0 && <LiveDashboard />}
            {tab === 1 && <AgentRoster />}
            {tab === 2 && <AttendanceRecords />}
            {tab === 3 && <AttendanceReport />}
            {tab === 4 && <BreakTypes />}
            {tab === 5 && isSuper && <AttendanceCorrections />}
            {tab === 6 && isSuper && <HrmsSync />}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminAgents;
